import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import ExitConfirmationModal from './modals/ExitConfirmationModal';
import LeaveWizardModal from './modals/LeaveWizardModal';
import { useAppDialog } from '../../context/DialogContext';
import { isWizardCompletedUser } from '../../utils/authRedirectUtils';
import { LuArrowLeft, LuLogOut, LuSave } from 'react-icons/lu';
import '../../styles/wizard.css';
import '../../styles/wizard-rt3.css';

// Public Wizard Layout - Minimal header for marketing/public steps
export const PublicWizardLayout = ({ children, showBack = false, onBack }) => {
  const navigate = useNavigate();
  const { confirm } = useAppDialog();

  const handleExit = async () => {
    const ok = await confirm({
      title: 'Exit setup?',
      body: 'Are you sure you want to leave? This setup session won’t be saved — you can start again anytime.',
      confirmLabel: 'Exit',
      cancelLabel: 'Stay',
      danger: true,
    });
    if (ok) navigate('/login');
  };

  return (
    <div className="wizard-layout public">
      <header className="wizard-header">
        <div className="wizard-header-content">
          <div className="wizard-logo">
            <img src="/robot-logo.png" alt="" />
            <span className="wizard-logo-text">Robitest</span>
            <span className="ob-header-tag">Setup</span>
          </div>
          <button type="button" className="wizard-exit-btn" onClick={handleExit}>
            <LuLogOut size={15} aria-hidden="true" />
            Exit
          </button>
        </div>
      </header>
      <main className="wizard-main">
        {children}
      </main>
      <footer className="wizard-footer">
        <p>&copy; {new Date().getFullYear()} RobiTest. All rights reserved.</p>
      </footer>
    </div>
  );
};

// Private Wizard Layout - For authenticated wizard steps (sidebar hidden)
export const PrivateWizardLayout = ({ children, showBack = false, onBack }) => {
  const navigate = useNavigate();
  const [showExitModal, setShowExitModal] = useState(false);

  const handleExit = () => {
    setShowExitModal(true);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
  };

  return (
    <div className="wizard-layout private">
      <header className="wizard-header">
        <div className="wizard-header-content">
          <div className="wizard-logo">
            <img src="/robot-logo.png" alt="" />
            <span className="wizard-logo-text">Robitest</span>
            <span className="ob-header-tag">Setup</span>
          </div>
          <div className="wizard-header-actions">
            {showBack && (
              <button type="button" className="wizard-back-btn" onClick={handleBack}>
                <LuArrowLeft size={15} aria-hidden="true" />
                Back
              </button>
            )}
            <button type="button" className="wizard-save-exit-btn" onClick={handleExit}>
              <LuSave size={15} aria-hidden="true" />
              Save & Exit
            </button>
          </div>
        </div>
      </header>
      <main className="wizard-main">
        {children}
      </main>
      
      {/* Wizard-completed users just leave to the app; others save the draft and log out */}
      {isWizardCompletedUser() ? (
        <LeaveWizardModal
          isOpen={showExitModal}
          onClose={() => setShowExitModal(false)}
        />
      ) : (
        <ExitConfirmationModal
          isOpen={showExitModal}
          onClose={() => setShowExitModal(false)}
        />
      )}
    </div>
  );
};

// Wizard Container - wraps content with consistent styling
export const WizardContainer = ({ children, className = '' }) => {
  return (
    <div className={`wizard-container ${className}`}>
      {children}
    </div>
  );
};

// Wizard Card - for step content
export const WizardCard = ({ children, className = '', title, subtitle }) => {
  return (
    <div className={`wizard-card ${className}`}>
      {(title || subtitle) && (
        <div className="wizard-card-header">
          {title && <h2 className="wizard-card-title">{title}</h2>}
          {subtitle && <p className="wizard-card-subtitle">{subtitle}</p>}
        </div>
      )}
      <div className="wizard-card-content">
        {children}
      </div>
    </div>
  );
};

// Wizard Actions - for buttons at bottom of step
export const WizardActions = ({ 
  children, 
  showBack = false, 
  onBack, 
  showNext = true, 
  onNext, 
  nextLabel = 'Continue', 
  nextDisabled = false,
  nextLoading = false,
  className = '' 
}) => {
  const onlyNextVisible = !showBack && showNext;

  return (
    <div
      className={[
        'wizard-actions',
        onlyNextVisible ? 'wizard-actions-next-only' : '',
        className
      ].filter(Boolean).join(' ')}
    >
      {showBack && (
        <button 
          type="button" 
          className="wizard-btn wizard-btn-secondary"
          onClick={onBack}
        >
          <LuArrowLeft size={16} aria-hidden="true" />
          Back
        </button>
      )}
      {showNext && (
        <button 
          type="button" 
          className="wizard-btn wizard-btn-primary"
          onClick={onNext}
          disabled={nextDisabled || nextLoading}
        >
          {nextLoading ? (
            <>
              <span className="spinner-small" aria-hidden="true"></span>
              Loading...
            </>
          ) : (
            nextLabel
          )}
        </button>
      )}
      {children}
    </div>
  );
};

// Progress Bar
export const WizardProgress = ({ current, total }) => {
  const percentage = (current / total) * 100;
  
  return (
    <div className="wizard-progress">
      <div className="wizard-progress-bar">
        <div 
          className="wizard-progress-fill" 
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="wizard-progress-text">
        Step {current} of {total}
      </span>
    </div>
  );
};

export default {
  PublicWizardLayout,
  PrivateWizardLayout,
  WizardContainer,
  WizardCard,
  WizardActions,
  WizardProgress
};